import { ReactNode } from "react";

type RichTextComponents = {
  [tag: string]: (chunks: string) => ReactNode;
};

export const useRichText = () => {
  const rich = (text: string, components: RichTextComponents): ReactNode[] => {
    // This will hold our final array of strings and React elements
    const result: ReactNode[] = [];
    // Remaining text to process
    let remainingText = text;

    // Continue processing until we've gone through all the text
    while (remainingText.length > 0) {
      // Try to find the earliest tag in the remaining text
      let earliestMatch: {
        tag: string;
        startIndex: number;
        endTagIndex: number;
      } | null = null;

      // Check each component tag
      Object.keys(components).forEach(tag => {
        const startTag = `<${tag}>`;
        const endTag = `</${tag}>`;
        const startIndex = remainingText.indexOf(startTag);

        // If we found a start tag
        if (startIndex !== -1) {
          // Find the corresponding end tag
          const contentStartIndex = startIndex + startTag.length;
          const endTagIndex = remainingText.indexOf(endTag, contentStartIndex);

          // Only consider valid matches (with both start and end tags)
          if (endTagIndex !== -1) {
            // If this match is earlier than any previous match, or we don't have a match yet
            if (
              earliestMatch === null ||
              startIndex < earliestMatch.startIndex
            ) {
              earliestMatch = { tag, startIndex, endTagIndex };
            }
          }
        }
      });

      // If we found a tag to process
      if (earliestMatch) {
        const { tag, startIndex, endTagIndex } = earliestMatch;
        const startTag = `<${tag}>`;
        const endTag = `</${tag}>`;

        // Add any text before the tag
        if (startIndex > 0) {
          result.push(remainingText.substring(0, startIndex));
        }

        // Get the content inside the tags
        const contentStartIndex = startIndex + startTag.length;
        const content = remainingText.substring(contentStartIndex, endTagIndex);

        // Render the component with the content
        const rendered = components[tag](content);
        result.push(rendered);

        // Update the remaining text to process
        remainingText = remainingText.substring(endTagIndex + endTag.length);
      } else {
        // No more tags found, add the remaining text and exit
        result.push(remainingText);
        break;
      }
    }

    return result;
  };

  return { rich };
};
